import { useEffect, useRef } from 'react';
import { useToast } from './ui/use-toast';
import { useRestaurant } from '../context/RestaurantContext';
import apiClient from '../services/api';
import { playNotificationBeep } from '../lib/notifySound';
import { isMuted } from '../lib/notifyMute';
import { subscribeRealtime, fallbackPoll } from '../lib/realtime';

// Safety-net poll: the WebSocket normally delivers within a second, so this only covers a socket that can't connect.
const POLL_MS = 20000;

/** Background watcher mounted once in the app shell: polls pending requests and toasts staff when a new one arrives. */
export default function ReservationRequestsWatcher() {
  const { restaurantId } = useRestaurant();
  const { toast } = useToast();
  // Baseline: ids seen so far — only genuinely new requests raise a toast.
  const seenIds = useRef(null);

  useEffect(() => {
    if (!restaurantId) return undefined;
    let cancelled = false;

    const check = async () => {
      try {
        const { data } = await apiClient.get(`/users/restaurant/${restaurantId}/reservations/pending/`);
        if (cancelled || !data?.success) return;
        const ids = new Set((data.results || []).map((r) => r.id));
        if (seenIds.current === null) {
          // First poll of the session: don't toast the backlog, just remember it.
          seenIds.current = ids;
          return;
        }
        const fresh = (data.results || []).filter((r) => !seenIds.current.has(r.id));
        seenIds.current = ids;
        // Muted: the baseline still advanced, so unmuting later doesn't replay a backlog of stale toasts.
        if (fresh.length === 0 || isMuted()) return;
        playNotificationBeep();
        fresh.forEach((r) => {
          const when = r.reserved_start_at
            ? new Date(r.reserved_start_at).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
            : '';
          toast({
            title: '📅 New reservation request',
            description: `${r.customer_name || 'A guest'} · ${r.pax || '?'} pax${when ? ` · ${when}` : ''}${r.dining_type === 'BUFFET' ? ` · Buffet${r.buffet_name ? `: ${r.buffet_name}` : ''}` : ''} — approve it on All Reservations, or from the bell.`,
            duration: 12000,
          });
        });
      } catch { /* silent — next poll retries */ }
    };

    check();
    // Realtime: re-check the instant a reservation is created/changed.
    const unsub = subscribeRealtime(['reservations'], () => check());
    // A request must not wait twenty seconds because a proxy dropped the socket.
    const stopPoll = fallbackPoll(check, POLL_MS);
    return () => { cancelled = true; unsub(); stopPoll(); };
  }, [restaurantId, toast]);

  return null;
}
